import { can, scopeOf, type PermissionAction, type PermissionScope, type ScopedAction } from '@fint/validation';
import { useActiveOrg } from '@/stores/auth';

/**
 * What the current user may do in the current org.
 *
 * ── WHAT THIS IS FOR, AND WHAT IT IS NOT ──
 * This hides controls. It does NOT protect anything. The server checks the same
 * permission on every request, and that check is the security boundary; this
 * only stops the UI offering a button that would come back 403.
 *
 * Which is worth doing: a "Delete" that always fails is worse than no Delete.
 * But nothing here may ever be the only thing standing between a user and an
 * action - a hidden button is one devtools edit away from being visible.
 */
export function usePermissions() {
  const org = useActiveOrg();
  const permissions = org?.permissions;

  return {
    /** May they do this at all? */
    can: (resource: string, action: PermissionAction): boolean =>
      permissions ? can(permissions, resource, action) : false,

    /** 'none' | 'own' | 'all' for a scoped action. */
    scope: (resource: string, action: ScopedAction): PermissionScope =>
      permissions ? scopeOf(permissions, resource, action) : 'none',

    /** True when they see the whole org rather than only their own records. */
    seesAll: (resource: string, action: ScopedAction = 'view'): boolean =>
      permissions ? scopeOf(permissions, resource, action) === 'all' : false,

    isAdmin: org?.role === 'admin',
    ready: !!permissions,
  };
}
